import React, { useState, useEffect, useRef } from "react";
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import CodeIcon from '@mui/icons-material/Code';
import { categories, resume, formatForJSON } from "../../../portfolio";
import { generateProfessionalResume } from "./PdfGenerator";
import { generateATSFriendlyResume } from "./ATS-PdfGenerator";
import "./ResumePage.css";

const ResumePage = () => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const downloadContainerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadContainerRef.current && !downloadContainerRef.current.contains(event.target)) {
        setShowDownloadOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDownloadPDF = async (isATS = false) => {
    setIsGeneratingPDF(true);

    try {
      // Use centralized resume data from portfolio.js
      const resumeData = {
        name: resume.personalInfo.name,
        title: resume.personalInfo.title,
        email: resume.personalInfo.email,
        phone: resume.personalInfo.phone,
        location: resume.personalInfo.location,
        summary: resume.personalInfo.summary,
        experience: resume.experience,
        education: resume.education,
        skills: resume.skills,
        certifications: resume.certifications
      };

      // Generate PDF using appropriate theme
      const doc = isATS ? generateATSFriendlyResume(resumeData) : generateProfessionalResume(resumeData);

      // Save the PDF
      const filename = isATS ? 'Abhinav_Chaturvedi_Resume_ATS_Friendly.pdf' : 'Abhinav_Chaturvedi_Resume_Professional.pdf';
      doc.save(filename);

    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to print dialog
      window.print();
    } finally {
      setIsGeneratingPDF(false);
    }
    setShowDownloadOptions(false);
  };

  const handlePrint = () => {
    window.print();
    setShowDownloadOptions(false);
  };

  const handleDownloadJSON = () => {
    // Use centralized formatter for JSON Resume format
    const resumeData = formatForJSON();

    const dataStr = JSON.stringify(resumeData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = 'abhinav_chaturvedi_resume.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    setShowDownloadOptions(false);
  };

  const downloadOptions = [
    {
      label: 'ATS-Friendly PDF',
      icon: <PictureAsPdfIcon />,
      action: () => handleDownloadPDF(true),
      description: 'Simple format optimized for ATS systems'
    },
    {
      label: 'Professional PDF',
      icon: <PictureAsPdfIcon />,
      action: () => handleDownloadPDF(false),
      description: 'Styled format for human readers'
    },
    {
      label: 'Print Resume',
      icon: <PrintIcon />,
      action: handlePrint,
      description: 'Print or save as PDF'
    },
    {
      label: 'JSON Resume',
      icon: <CodeIcon />,
      action: handleDownloadJSON,
      description: 'Machine-readable format'
    }
  ];

  return (
    <div className="resume-page">
      {/* Hero Section */}
      <section className="section resume-hero">
        <div className="resume-hero__content">
          <h1 className="resume-hero__title">Resume</h1>
          <p className="resume-hero__subtitle">
            {resume.personalInfo.title} with 12.5+ years of experience
          </p>
          <div className={`resume-download-container ${showDownloadOptions ? 'open' : ''}`} ref={downloadContainerRef}>
            <button
              className="btn btn--outline resume-hero__download"
              onClick={() => setShowDownloadOptions(!showDownloadOptions)}
              disabled={isGeneratingPDF}
            >
              <DownloadIcon />
              {isGeneratingPDF ? 'Generating PDF...' : 'Download Resume'}
              <KeyboardArrowDownIcon className="download-arrow" />
            </button>

            {showDownloadOptions && (
              <div className="download-options">
                {downloadOptions.map((option, index) => (
                  <button
                    key={index}
                    className="download-option"
                    onClick={option.action}
                    disabled={isGeneratingPDF}
                  >
                    <div className="download-option__icon">
                      {option.icon}
                    </div>
                    <div className="download-option__content">
                      <span className="download-option__label">{option.label}</span>
                      <span className="download-option__description">{option.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="section resume-nav">
        <div className="resume-nav__container">
          <a href="#education" className="resume-nav__link">Education</a>
          <a href="#experience" className="resume-nav__link">Experience</a>
          <a href="#skills" className="resume-nav__link">Skills</a>
          <a href="#certifications" className="resume-nav__link">Certifications</a>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="section resume-section">
        <h2 className="section__title">Education</h2>
        {resume.education.map((edu, index) => (
          <div key={index} className="resume-card">
            <div className="resume-card__header">
              <h3 className="resume-card__title">{edu.degree}</h3>
              <span className="resume-card__period">{edu.period}</span>
            </div>
            <div className="resume-card__content">
              <p className="resume-card__company">
                <a
                  href={edu.url}
                  className="resume-card__link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {edu.institution}
                </a>
              </p>
            </div>
          </div>
        ))}
      </section>

      {/* Experience Section */}
      <section id="experience" className="section resume-section">
        <h2 className="section__title">Experience</h2>

        {resume.experience.map((exp, index) => (
          <div key={index} className="resume-card">
            <div className="resume-card__header">
              <h3 className="resume-card__title">{exp.position}</h3>
              <span className="resume-card__period">{exp.period}</span>
            </div>
            <div className="resume-card__content">
              <p className="resume-card__company">
                <a
                  href={exp.url}
                  className="resume-card__link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {exp.company}
                </a>
              </p>
              <ul className="resume-card__achievements">
                {exp.achievements.map((achievement, achIndex) => (
                  <li key={achIndex}>{achievement}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* Skills Section */}
      <section id="skills" className="section resume-section">
        <h2 className="section__title">Skills</h2>
        <div className="skills-grid">
          {resume.skills.map((skill, index) => (
            <div key={skill.title} className="skill-card">
              <div className="skill-card__header">
                <span className="skill-card__name">{skill.title}</span>
                <span className="skill-card__level">{skill.competency}/5</span>
              </div>
              <div className="skill-card__bar">
                <div
                  className="skill-card__progress"
                  style={{
                    width: `${(skill.competency / 5) * 100}%`,
                    backgroundColor: categories.find(category =>
                      skill.category.includes(category.name)
                    )?.color || '#3182ce',
                  }}
                />
              </div>
              <div className="skill-card__categories">
                {skill.category.slice(0, 2).map(cat => (
                  <span key={cat} className="skill-card__category">{cat}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="section resume-section">
        <h2 className="section__title">Certifications</h2>
        <div className="certifications-grid">
          {resume.certifications.map((cert, index) => (
            <div key={index} className="certification-card">
              <h3 className="certification-card__title">{cert.title}</h3>
              <p className="certification-card__id">Credential ID: {cert.id}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ResumePage;
